/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, June 29th 2022, 12:16:46 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { JSONConfigurationParams } from "common/components/JSONConfigurationParams";
import { useControlledInput, OSFormInputProps, FormInputCustomErrorWrapper } from "ui-widgets/forms";
import { InputHelpDialogWrapper } from "ui-widgets";
import { ObjectKey } from "lib/types";
import { ProcessDefinition, ProcessDefinitionExtraConfigurations } from "lib/schemas";
import { COLORSCHEME } from "theme/styles";

export const ExtraConfigurationsSettingsField = ({
  extra_configurations,
}: {
  extra_configurations: ProcessDefinitionExtraConfigurations;
}) => {
  const schema_field_key: OSFormInputProps<ProcessDefinition>["schema_field_key"] = "extra_configurations";

  const {
    useFormReturn,
    field: { value },
  } = useControlledInput<ProcessDefinition>({ schema_field_key });

  const {
    formState: { errors },
  } = useFormReturn;

  const onChangeInitiatedByWorker = (event: React.ChangeEvent<HTMLInputElement>) => {
    useFormReturn.setValue(schema_field_key, { ...value, is_initiated_by_worker: event.target.checked });
  };

  const onChangeWorkerCanSubmitForClient = (event: React.ChangeEvent<HTMLInputElement>) => {
    useFormReturn.setValue(schema_field_key, { ...value, worker_can_submit_for_client: event.target.checked });
  };

  const onChangeExcludeFromMenu = (event: React.ChangeEvent<HTMLInputElement>) => {
    useFormReturn.setValue(schema_field_key, { ...value, exclude_from_services_menu: event.target.checked });
  };

  const onChangeSideMenuIcon = (event: React.ChangeEvent<HTMLInputElement>) => {
    useFormReturn.setValue(schema_field_key, { ...value, service_side_menu_icon: event.target.value });
  };

  const onChangeCallToActionText = (event: React.ChangeEvent<HTMLInputElement>) => {
    useFormReturn.setValue(schema_field_key, { ...value, call_to_action_text: event.target.value });
  };

  const watchedConfigurations = useFormReturn.watch("extra_configurations");
  const customConfigurations = (watchedConfigurations as any)?.custom_settings || {};

  const onChangeCustomConfiguration: React.ComponentProps<
    typeof JSONConfigurationParams
  >["onChangeCustomConfiguration"] = (config) => {
    useFormReturn.setValue<any>("extra_configurations", { ...watchedConfigurations, custom_settings: config });
  };

  return (
    <Stack spacing={2}>
      <InputHelpDialogWrapper
        helpTitle="Call to action text"
        helpContent="The call to action text for the service. This defaults to 'Apply' if no value is given."
      >
        <TextField
          label="Call to action text"
          defaultValue={extra_configurations?.call_to_action_text}
          onChange={onChangeCallToActionText}
        />
      </InputHelpDialogWrapper>
      <ExtraConfigurationsFieldErrorWrapper errors={errors} json_field_key="call_to_action_text" />

      <FormGroup>
        <InputHelpDialogWrapper
          helpTitle="Initiated by worker"
          helpContent="Check this field if you want the process defined by this service to be initiated by a worker in your team instead of an applicant/client."
        >
          <FormControlLabel
            control={
              <Checkbox
                defaultChecked={extra_configurations?.is_initiated_by_worker}
                onChange={onChangeInitiatedByWorker}
              />
            }
            label={<>Initiated by worker</>}
          />
        </InputHelpDialogWrapper>
      </FormGroup>
      <ExtraConfigurationsFieldErrorWrapper errors={errors} json_field_key="is_initiated_by_worker" />

      <FormGroup>
        <InputHelpDialogWrapper
          helpTitle="Worker can submit on a client's behalf"
          helpContent="Check this field if you want to give worker's the ability to make submissions on behalf of clients to trigger process instances. Only workers with permission to access this process and specific permission to 'Make Submissions on other user's behalf' will be able to make these submissions"
        >
          <FormControlLabel
            control={
              <Checkbox
                defaultChecked={extra_configurations?.worker_can_submit_for_client}
                onChange={onChangeWorkerCanSubmitForClient}
              />
            }
            label={<>Worker can submit on a client's behalf</>}
          />
        </InputHelpDialogWrapper>
      </FormGroup>
      <ExtraConfigurationsFieldErrorWrapper errors={errors} json_field_key="worker_can_submit_for_client" />

      <FormGroup>
        <InputHelpDialogWrapper
          helpTitle="Exclude from services menu"
          helpContent="Check this field if you want to exclude this service from the services menu. This means that the service will not be visible to clients and workers on the standard Onestop portal interface menu for services, but will still be accessible via direct link, any service categories listing or the services list page. Only use this if you intend on customizing this service's access on the portal."
        >
          <FormControlLabel
            control={
              <Checkbox
                defaultChecked={extra_configurations?.exclude_from_services_menu}
                onChange={onChangeExcludeFromMenu}
              />
            }
            label={<>Exclude from services menu</>}
          />
        </InputHelpDialogWrapper>
      </FormGroup>
      <ExtraConfigurationsFieldErrorWrapper errors={errors} json_field_key="exclude_from_services_menu" />

      <FormGroup>
        <InputHelpDialogWrapper
          helpTitle="Side Menu Icon"
          helpContent="The side menu icon to be used for your service on the default customer and staff portals that come with Onestop. Provide a valid font-awesome, material design or bootsrap css icon class."
        >
          <TextField
            label="Side Menu Icon"
            defaultValue={extra_configurations?.service_side_menu_icon}
            onChange={onChangeSideMenuIcon}
          />
        </InputHelpDialogWrapper>
      </FormGroup>
      <ExtraConfigurationsFieldErrorWrapper errors={errors} json_field_key="service_side_menu_icon" />
      <Typography variant="h5" color={COLORSCHEME.primary}>
        Add custom configurations
      </Typography>
      <Divider />
      <JSONConfigurationParams
        params={customConfigurations}
        onChangeCustomConfiguration={onChangeCustomConfiguration}
      />
    </Stack>
  );
};

interface ExtraConfigurationsFieldErrorWrapperProps {
  errors: any;
  json_field_key: ObjectKey<ProcessDefinitionExtraConfigurations>;
}

const ExtraConfigurationsFieldErrorWrapper = ({ errors, json_field_key }: ExtraConfigurationsFieldErrorWrapperProps) =>
  errors?.registration_form_settings?.[json_field_key] ? (
    <FormInputCustomErrorWrapper>
      <>{errors?.registration_form_settings?.[json_field_key]?.message}</>
    </FormInputCustomErrorWrapper>
  ) : null;
